import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { AdminStats } from './AdminStats';
import { UserManagement } from './UserManagement';
import { PromoManagement } from './PromoManagement';
import { AffiliateVendorManagement } from './AffiliateManagement';
import { AffiliateManagement as AffiliatePayoutManagement } from './AffiliatePayoutManagement';
import { SubscriptionManagement } from './SubscriptionManagement';
import { SystemSettings } from './SystemSettings';
import { ActivityLogs } from './ActivityLogs';
import { KnowledgeBaseManagement } from './KnowledgeBaseManagement';
import { CategoryManagement } from './CategoryManagement';
import { CategoryAnalytics } from './CategoryAnalytics';
import { VoiceAnalytics } from './VoiceAnalytics';
import { FacilityManagement } from './FacilityManagement';
import { EducationManagement } from './EducationManagement';
import { LessonMediaManagement } from './LessonMediaManagement';
import { StudentEducationLogs } from './StudentEducationLogs';
import { UniversityManagement } from './UniversityManagement';
import { HardshipApplicationsAdmin } from './Hardshipapplicationsadmin';
import { SponsorshipAdmin } from './SponsorshipAdmin';
import { SupportManagement } from './SupportManagement';

interface AdminDashboardProps {
  user: {
    id: string;
    email?: string;
    name?: string;
  };
}

type AdminTab = 'dashboard' | 'users' | 'support' |'promos'| 'facilities' | 'schools' | 'education' | 'lesson-media' | 'university' | 'edu-logs' | 'category-analytics' | 'voice-analytics' | 'affiliates' | 'affiliate-vendors' | 'subscriptions' | 'settings' | 'logs' | 'knowledge' | 'categories' | 'scholarships' | 'sponsorships' ;

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [openTicketCount, setOpenTicketCount] = useState<number>(0);
  const [escalatedCount, setEscalatedCount] = useState<number>(0);

  const fetchTicketsCount = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('support', {
        body: { action: 'admin_list_threads' }
      });
      if (!error && data?.threads) {
        const threads = data.threads as Array<{ status: string; escalated: boolean }>;
        const open = threads.filter(t => t.status === 'open' || (t.escalated && t.status !== 'resolved'));
        const escalated = threads.filter(t => t.escalated && t.status !== 'resolved');
        setOpenTicketCount(open.length);
        setEscalatedCount(escalated.length);
      }
    } catch (e) {
      console.warn('Failed to load support tickets count:', e);
    }
  }, []);

  useEffect(() => {
    fetchTicketsCount();
    const interval = setInterval(fetchTicketsCount, 30000);
    return () => clearInterval(interval);
  }, [fetchTicketsCount]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchTicketsCount();
    }
  }, [activeTab, fetchTicketsCount]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'support', label: 'Support Tickets', icon: '💬' },
    { id: 'promos', label: 'Promo Codes', icon: '🎟️' },
    { id: 'schools', label: 'Schools & Hubs', icon: '🏫' },
    { id: 'facilities', label: 'Elderly Care', icon: '🏢' },
    { id: 'education', label: 'Edu Management', icon: '🎓' },
    { id: 'lesson-media', label: 'Lesson Visuals', icon: '🖼️' },
    { id: 'scholarships', label: 'Hardship Applications', icon: '🤝' },
    { id: 'sponsorships', label: 'Sponsorships', icon: '💚' },
    { id: 'university', label: 'University', icon: '🏛️' },
    { id: 'edu-logs', label: 'Education Logs', icon: '📝🎓' },
    { id: 'category-analytics', label: 'Category Analytics', icon: '📈' },
    { id: 'voice-analytics', label: 'Voice Analytics', icon: '🎤' },
    { id: 'affiliates', label: 'Affiliate Partners', icon: '🤝' },
    { id: 'affiliate-vendors', label: 'Affiliate Vendors', icon: '🔗' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '💳' },
    { id: 'categories', label: 'Categories', icon: '📂' },
    { id: 'knowledge', label: 'Knowledge Base', icon: '📚' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'logs', label: 'Activity Logs', icon: '📝' }
  ] as const;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <AdminStats
            onNavigateTab={(tab) => setActiveTab(tab as AdminTab)}
            ticketStats={{ open: openTicketCount, escalated: escalatedCount }}
          />
        );
      case 'users':
        return <UserManagement />;
      case 'support':
        return <SupportManagement />;
      case 'promos':
        return <PromoManagement />;
      case 'facilities':
        return <FacilityManagement typeFilter="elderly" />;
      case 'schools':
        return <FacilityManagement typeFilter="education" />;
      case 'education':
        return <EducationManagement />;
      case 'lesson-media':
        return <LessonMediaManagement />;
      case 'scholarships':
        return <HardshipApplicationsAdmin />;
      case 'sponsorships':
        return <SponsorshipAdmin />;
      case 'university':
        return <UniversityManagement />;
      case 'edu-logs':
        return <StudentEducationLogs />;
      case 'category-analytics':
        return <CategoryAnalytics />;
      case 'voice-analytics':
        return <VoiceAnalytics />;
      case 'affiliates':
        return <AffiliatePayoutManagement />;
      case 'affiliate-vendors':
        return <AffiliateVendorManagement />;
      case 'subscriptions':
        return <SubscriptionManagement />;
      case 'knowledge':
        return <KnowledgeBaseManagement />;
      case 'categories':
        return <CategoryManagement />;
      case 'settings':
        return <SystemSettings />;
      case 'logs':
        return <ActivityLogs />;
      default:
        return <AdminStats />;
    }
  };


  return (
    <div className="dark admin-portal min-h-screen bg-background text-foreground pb-20">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="glass-panel mx-4 sm:mx-6 lg:mx-8 mt-6 mb-8"
      >
        <div className="w-full px-6 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-black text-foreground tracking-tight">Admin Dashboard</h1>
              <p className="text-muted-foreground font-medium mt-1">Manage your Still Waters Academy platform</p>
            </div>
            <div className="flex items-center space-x-4 bg-card/70 p-2 pr-4 rounded-full border border-border shadow-sm">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <span className="text-white font-bold text-sm">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-foreground">{user?.email}</p>
                <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wider">Administrator</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Navigation Tabs */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8 overflow-x-auto scrollbar-hide"
        >
          <div className="glass-panel p-2 inline-flex space-x-2">
            {tabs.map((tab) => {
              const isSupport = tab.id === 'support';
              const hasBadge = isSupport && openTicketCount > 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-3 px-5 rounded-2xl font-bold text-sm transition-all duration-300 whitespace-nowrap ${activeTab === tab.id
                    ? 'bg-slate-800 text-white shadow-lg shadow-slate-900/10 scale-100 dark:bg-slate-700'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground scale-95 hover:scale-100'
                    }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                  {hasBadge && (
                    <span
                      className={`ml-1.5 px-2 py-0.5 text-xs font-black rounded-full shadow-md flex items-center gap-1 ${
                        escalatedCount > 0
                          ? 'bg-rose-500 text-white animate-pulse shadow-rose-500/50'
                          : 'bg-amber-500 text-slate-950 font-black shadow-amber-500/30'
                      }`}
                    >
                      {escalatedCount > 0 && <span className="text-[10px]">🆘</span>}
                      {openTicketCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Tab Content - Full Width */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="w-full bg-card/40 dark:bg-slate-900/40 backdrop-blur-md rounded-[2.5rem] border border-border shadow-xl overflow-hidden min-h-[500px]"
        >
          {renderTabContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
